import React, { type ReactNode } from 'react';
import { CountdownRing } from './CountdownRing';
type SlideFrameProps = {
  eyebrow: string;
  index: number;
  /** Hide the corner countdown on slides that already show the hero ring. */
  showMiniCountdown?: boolean;
  children: ReactNode;
};

/**
 * Shared 16:9 full-bleed slide chrome: deep-green field, dot grid, soft green
 * bloom, thin arcs, and the persistent eyebrow / brand / footer rails.
 */
export function SlideFrame({
  eyebrow,
  index,
  showMiniCountdown = true,
  children
}: SlideFrameProps) {
  return <section className="relative h-full w-full overflow-hidden" style={{
    backgroundColor: 'var(--sv-bg)'
  }} aria-label={eyebrow} data-id="element-29" data-mp="36cg126eaz662">
      {/* dot grid */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.35]" style={{
      backgroundImage: 'radial-gradient(rgba(255,255,255,0.10) 1px, transparent 1px)',
      backgroundSize: '26px 26px'
    }} data-id="element-30" data-mp="3g80vnjh1l45s" />
      {/* green bloom */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{
      background: 'radial-gradient(75% 60% at 78% 18%, rgba(74,222,128,0.16) 0%, rgba(5,17,12,0) 62%)'
    }} data-id="element-31" data-mp="2ed64eauwkt2g" />
      {/* thin arcs */}
      <svg aria-hidden="true" viewBox="0 0 1600 900" preserveAspectRatio="none" className="pointer-events-none absolute inset-0 h-full w-full" data-id="element-32" data-mp="2s416936zi9wj">
        <circle cx="1180" cy="-140" r="720" fill="none" stroke="rgba(74,222,128,0.20)" strokeWidth="2" data-id="element-33" data-mp="1zfvlwhjuemx8" />
        <circle cx="140" cy="1080" r="620" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="2" data-id="element-34" data-mp="29el4ydnh6df2" />
      </svg>

      <div className="relative flex h-full w-full flex-col px-[6%] py-[4.5%]" data-id="element-35" data-mp="3fwrgn7z8ol4p">
        <header className="flex shrink-0 items-center justify-between" data-id="element-36" data-mp="3ks6vo38zgn2p">
          <p className="text-[clamp(9px,0.85vw,15px)] font-medium uppercase tracking-[0.32em] text-white/45" data-id="element-37" data-mp="2v4kwwj4rdp1d">
            {eyebrow}
            <span className="text-[color:var(--sv-green)]" data-id="element-38" data-mp="2q69omcu43ml9">
              {' '}
              · {index} of 6
            </span>
          </p>
          <div className="flex items-center gap-[1.1vw]" data-id="element-39" data-mp="304ywrdw7hsob">
            <p className="text-[clamp(9px,0.85vw,15px)] font-semibold uppercase tracking-[0.32em] text-white/70" data-id="element-40" data-mp="305h1z00ujh8d">
              Solventum Dental
            </p>
            {showMiniCountdown ? <CountdownRing size="sm" data-id="element-41" data-mp="27hl59vba1pbl" /> : null}
          </div>
        </header>

        <div className="flex min-h-0 flex-1 flex-col justify-center" data-id="element-42" data-mp="2m5zh2vqo8o4a">
          {children}
        </div>

        <footer className="shrink-0 text-[clamp(8px,0.7vw,12px)] tracking-wide text-white/25" data-id="element-43" data-mp="2hd69m03tj1oi">
          For dental professionals. Internal strategic concept · © Solventum 2026
        </footer>
      </div>
    </section>;
}